import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  RefreshControl,
  ScrollView,
  StatusBar,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';

import SimpleHeader from '../../components/SimpleHeader';
import StockIcon from '../../components/UIComponents/StockIcon';
import TabBar from '../../components/TabBar';
import TokenListCard from '../../components/UIComponents/TokenListCard';
import { useTokens } from '../../hooks/useTokens';
import { useXStocks } from '../../hooks/useXStocks';
import { v2GetRates, v2GetRatesHistory } from '../../services/v2/api';
import { getFavoriteTokenSymbols } from '../../services/tokenFavorites';
import type { V2RateHistoryPoint, V2RateInfo, V2Token, V2XStock } from '../../types/v2';
import { COLORS } from '../../theme/themeTokens';
import { getTokenIconSource , getLocalTokenIcon} from '../../tools/getCurrencyIcon';
import { buildUniqueMarketTokens } from '../../utils/marketChart';
import {
  CHART_WIDTH, COLUMN_GAP, LIST_ICON_COLUMN, NAME_MIN_WIDTH, PRICE_WIDTH,
} from '../../components/UIComponents/tokenRowLayout';
import { xStockTicker } from '../../utils/xstocks';

// With xStocks on, the design replaces Popular with All (tokens and stocks
// together) and adds an xStocks tab; with it off the screen is unchanged.
type CatalogTab = 'favorites' | 'popular' | 'all' | 'tokens' | 'xstocks';

const TABS_DEFAULT: readonly (readonly [CatalogTab, string])[] = [
  ['favorites', '☆'],
  ['popular', 'Popular'],
  ['tokens', 'Tokens'],
];

const TABS_WITH_XSTOCKS: readonly (readonly [CatalogTab, string])[] = [
  ['favorites', '☆'],
  ['all', 'All'],
  ['tokens', 'Tokens'],
  ['xstocks', 'xStocks'],
];

interface PopularTokensScreenProps {
  onBack: () => void;
  onNavigate: (screen: string) => void;
  onTokenPress: (token: V2Token) => void;
  onStockPress?: (stock: V2XStock) => void;
}

/** One list row — a crypto token or an xStock. */
interface CatalogRow {
  kind: 'token' | 'xstock';
  /** Upper-case symbol: favorites and history keys. */
  key: string;
  ticker: string;
  name: string;
  iconUrl: string | null;
  isPopular: boolean;
  token?: V2Token;
  stock?: V2XStock;
}

const fetchHistoryInBatches = async (symbols: string[]) => {
  const batches: string[][] = [];
  for (let index = 0; index < symbols.length; index += 100) {
    batches.push(symbols.slice(index, index + 100));
  }
  const responses = await Promise.all(batches.map(batch => v2GetRatesHistory(batch, '7d')));
  return responses.reduce<Record<string, V2RateHistoryPoint[]>>((all, response) => ({
    ...all,
    ...response.series,
  }), {});
};

const PopularTokensScreen: React.FC<PopularTokensScreenProps> = ({
  onBack,
  onNavigate,
  onTokenPress,
  onStockPress,
}) => {
  const { tokens: apiTokens, loading: tokensLoading, error: tokensError, refresh } = useTokens();
  const xStocks = useXStocks();
  const [activeTab, setActiveTab] = useState<CatalogTab>('popular');
  const [favorites, setFavorites] = useState<string[]>([]);
  const [rates, setRates] = useState<Record<string, V2RateInfo>>({});
  const [history, setHistory] = useState<Record<string, V2RateHistoryPoint[]>>({});
  const [marketLoading, setMarketLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [marketError, setMarketError] = useState<string | null>(null);

  const tabs = xStocks.enabled ? TABS_WITH_XSTOCKS : TABS_DEFAULT;

  // Keep the active tab valid when the flag resolves or changes.
  useEffect(() => {
    if (xStocks.enabled && activeTab === 'popular') {
      setActiveTab('all');
    } else if (!xStocks.enabled && (activeTab === 'all' || activeTab === 'xstocks')) {
      setActiveTab('popular');
    }
  }, [activeTab, xStocks.enabled]);

  const catalog = useMemo(() => buildUniqueMarketTokens(apiTokens), [apiTokens]);
  const tokenSymbols = useMemo(() => catalog.map(item => item.symbol), [catalog]);
  const stockSymbols = useMemo(
    () => (xStocks.enabled ? xStocks.stocks.map(stock => stock.symbol) : []),
    [xStocks.enabled, xStocks.stocks],
  );
  const symbolsKey = [...tokenSymbols, ...stockSymbols].join(',');

  const rows = useMemo<CatalogRow[]>(() => [
    ...catalog.map(item => ({
      kind: 'token' as const,
      key: item.symbol,
      ticker: item.symbol,
      name: item.name,
      iconUrl: item.iconUrl,
      isPopular: item.isPopular,
      token: item.token,
    })),
    ...(xStocks.enabled ? xStocks.stocks : []).map(stock => ({
      kind: 'xstock' as const,
      key: stock.symbol.toUpperCase(),
      ticker: xStockTicker(stock.symbol),
      name: stock.name,
      iconUrl: stock.icon_url,
      isPopular: stock.is_popular,
      stock,
    })),
  ], [catalog, xStocks.enabled, xStocks.stocks]);

  useEffect(() => {
    getFavoriteTokenSymbols().then(setFavorites);
  }, []);

  const fetchMarketData = useCallback(async () => {
    if (tokenSymbols.length === 0 && stockSymbols.length === 0) {
      setRates({});
      setHistory({});
      setMarketLoading(false);
      return;
    }

    setMarketError(null);
    const [latestResult, historyResult] = await Promise.allSettled([
      tokenSymbols.length > 0 ? v2GetRates(tokenSymbols) : Promise.resolve({ rates: {}, updated_at: '' }),
      fetchHistoryInBatches([...tokenSymbols, ...stockSymbols]),
    ]);
    if (latestResult.status === 'fulfilled') {
      setRates(latestResult.value.rates);
    }
    if (historyResult.status === 'fulfilled') {
      setHistory(historyResult.value);
    }
    if (latestResult.status === 'rejected' && historyResult.status === 'rejected') {
      setMarketError('Market data is temporarily unavailable');
    }
    setMarketLoading(false);
  // symbolsKey intentionally represents the normalized catalog identity.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbolsKey]);

  useEffect(() => {
    fetchMarketData();
  }, [fetchMarketData]);

  // Stocks carry their own price; tokens are priced from /rates.
  const priceOf = useCallback((row: CatalogRow) => (
    row.kind === 'xstock'
      ? Number(row.stock?.price_usdt ?? 0)
      : Number(rates[row.key]?.price_usd ?? 0)
  ), [rates]);

  const changeOf = useCallback((row: CatalogRow) => (
    row.kind === 'xstock'
      ? Number(row.stock?.change_24h ?? 0)
      : Number(rates[row.key]?.change_24h ?? 0)
  ), [rates]);

  const displayedRows = useMemo(() => {
    const favoriteSet = new Set(favorites);
    const filtered = rows.filter(row => {
      switch (activeTab) {
        case 'favorites': return favoriteSet.has(row.key);
        case 'popular': return row.kind === 'token' && row.isPopular;
        case 'tokens': return row.kind === 'token';
        case 'xstocks': return row.kind === 'xstock';
        default: return true;
      }
    });
    return [...filtered].sort((a, b) => {
      if (activeTab === 'popular') {
        const priceDiff = priceOf(b) - priceOf(a);
        if (priceDiff !== 0) { return priceDiff; }
      }
      // Every tab reads alphabetically. The xStocks tab used to follow the
      // catalogue's sort_order instead, which is an operator's ordering and
      // tells a reader looking for a ticker nothing about where to look.
      return a.ticker.localeCompare(b.ticker, undefined, { sensitivity: 'base' });
    });
  }, [activeTab, favorites, priceOf, rows]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([refresh(), xStocks.refresh()]);
      await fetchMarketData();
      setFavorites(await getFavoriteTokenSymbols());
    } finally {
      setRefreshing(false);
    }
  }, [fetchMarketData, refresh, xStocks]);

  const initialLoading = tokensLoading || marketLoading || xStocks.loading;
  const error = tokensError || marketError;

  return (
    <View style={styles.container}>
      <StatusBar barStyle="dark-content" backgroundColor={COLORS.white} />
      <SimpleHeader title="Tokens" onBack={onBack} backgroundVariant="white" />

      <View style={styles.tabs}>
        {tabs.map(([key, label]) => (
          <TouchableOpacity
            key={key}
            style={[
              styles.tab,
              TAB_WIDTH_STYLES[key],
              activeTab === key && styles.activeTab,
            ]}
            onPress={() => setActiveTab(key)}
          >
            <Text style={[styles.tabText, activeTab === key && styles.activeTabText]}>{label}</Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Mirrors the row beneath it, column for column, so each label
          stands over what it names. The trend column lost its icon: a
          chart glyph over a row of charts said nothing the charts did not
          — the Dashboard list dropped it for the same reason. */}
      <View style={styles.columnHeader}>
        <View style={styles.iconColumn} />
        <Text style={[styles.columnLabel, styles.coinColumn]} numberOfLines={1}>
          {activeTab === 'xstocks' ? 'Tokenized stocks' : 'Coin'}
        </Text>
        <View style={styles.columnGap} />
        <Text style={[styles.columnLabel, styles.priceColumn]}>Price, USDT</Text>
        <View style={styles.columnGap} />
        <View style={styles.trendColumn} />
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={COLORS.primaryNavy} />}
      >
        {initialLoading ? (
          <ActivityIndicator size="large" color={COLORS.primaryNavy} style={styles.loadingIndicator} />
        ) : (
          <>
            {error && (
              <TouchableOpacity onPress={fetchMarketData} style={styles.retryRow}>
                <Text style={styles.retryText}>{error} · Retry</Text>
              </TouchableOpacity>
            )}
            {displayedRows.length === 0 ? (
              <Text style={styles.emptyText}>
                {activeTab === 'favorites'
                  ? 'Add tokens to Favorites from the token details screen'
                  : activeTab === 'xstocks' ? 'No stocks available yet' : 'No tokens found'}
              </Text>
            ) : displayedRows.map(row => (
              <TokenListCard
                key={`${row.kind}:${row.key}`}
                data={row.kind === 'xstock' ? {
                  iconNode: <StockIcon symbol={row.ticker} iconUrl={row.iconUrl} />,
                  symbol: row.ticker,
                  subtitle: row.name,
                  name: row.name,
                  rate: priceOf(row),
                  // Stocks carry their own display precision in the catalogue.
                  rateDecimals: row.stock?.display_decimals,
                  changePercent: changeOf(row),
                  history: history[row.key] ?? [],
                } : {
                  icon: getTokenIconSource(row.key, row.iconUrl),
                  iconFallback: getLocalTokenIcon(row.key),
                  symbol: row.ticker,
                  name: row.name,
                  rate: priceOf(row),
                  rateDecimals: row.token?.decimals,
                  changePercent: changeOf(row),
                  history: history[row.key] ?? [],
                }}
                onPress={() => {
                  if (row.kind === 'xstock' && row.stock) {
                    onStockPress?.(row.stock);
                  } else if (row.token) {
                    onTokenPress(row.token);
                  }
                }}
              />
            ))}
            <View style={styles.bottomSpacer} />
          </>
        )}
      </ScrollView>

      <TabBar activeTab="dashboard" onNavigate={onNavigate} />
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: COLORS.white },
  tabs: { flexDirection: 'row', paddingHorizontal: 16, gap: 6, paddingBottom: 10 },
  tab: { height: 32, borderRadius: 6, borderWidth: 1, borderColor: COLORS.primaryNavy, alignItems: 'center', justifyContent: 'center' },
  favoritesTab: { width: 48 },
  popularTab: { width: 86 },
  allTab: { width: 60 },
  tokensTab: { width: 80 },
  xstocksTab: { width: 86 },
  activeTab: { backgroundColor: COLORS.primaryNavy },
  tabText: { color: COLORS.primaryNavy, fontFamily: 'Inter', fontSize: 14, fontWeight: '500' },
  activeTabText: { color: COLORS.white },
  columnHeader: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 24, paddingBottom: 4 },
  columnLabel: { color: COLORS.blueSteel, fontFamily: 'Inter', fontSize: 14, fontWeight: '500' },
  // The row's own geometry (TokenListCard, tokenRowLayout): the name
  // takes the slack down to its floor, then a gap, the price
  // right-aligned, another gap, and the chart's width. The gaps and the
  // chart shrink here exactly as they do in the row, so the labels keep
  // standing over their columns on a narrow phone.
  coinColumn: { flex: 1, minWidth: NAME_MIN_WIDTH },
  columnGap: { width: COLUMN_GAP, flexShrink: 1 },
  // Empty, and the width of the row's icon: without it the name column
  // starts 32 further left than the names do, and once it is down to its
  // floor the labels slide off their columns.
  iconColumn: { width: LIST_ICON_COLUMN },
  priceColumn: { width: PRICE_WIDTH, textAlign: 'right' },
  trendColumn: { width: CHART_WIDTH, flexShrink: 1 },
  scrollView: { flex: 1 },
  scrollContent: { paddingHorizontal: 16 },
  loadingIndicator: { marginTop: 40 },
  retryRow: { paddingVertical: 12, alignItems: 'center' },
  retryText: { color: COLORS.brandOrange, fontSize: 14 },
  emptyText: { color: COLORS.blueSteel, fontSize: 14, textAlign: 'center', marginTop: 42, paddingHorizontal: 24 },
  bottomSpacer: { height: 110 },
});

const TAB_WIDTH_STYLES: Record<CatalogTab, object> = {
  favorites: styles.favoritesTab,
  popular: styles.popularTab,
  all: styles.allTab,
  tokens: styles.tokensTab,
  xstocks: styles.xstocksTab,
};

export default PopularTokensScreen;
